import { useCallback, useRef } from "react";
import {
  getAuthConfig,
  getAuthConfigByKey,
  useAuth,
  VITE_MODE_MAPPING,
  type AuthConfig,
  type LogoutReason,
} from "@webuild-ai/react-auth";
import {
  clearAppSessionData,
  clearMsalCache,
  getEndSessionEndpoint,
  logLogoutEvent,
} from "@webuild-ai/react-auth/services";

import { useEmbeddedAuthConfig } from "@/providers/EmbeddedAuthProvider";
import { logger } from "@/telemetry";
import { redirectTo } from "@/utils/redirectTo";
import { clearSkillListCache } from "@/hooks/chat/useSkillList";
import { clearWorkflowListCache } from "@/hooks/chat/useWorkflowList";

/**
 * How long the single-flight guard is held before the attempt is assumed to have failed.
 *
 * Neither `clearMsalCache()` nor `window.location.assign` reliably throws when a sign-out is
 * blocked. MSAL swallows its own errors, and `assign` is a silent no-op under a CSP or COOP
 * restriction, a popup blocker or an interfering extension. A `catch` alone therefore cannot
 * release the guard, and without this watchdog a blocked sign-out would leave the user unable
 * to retry for the remaining life of the mount.
 *
 * The window only has to outlast the synchronous work plus the browser starting navigation,
 * so it is generous rather than tuned.
 */
const LOGOUT_WATCHDOG_MS = 10_000;

/**
 * Resolve the auth config the same way `AuthProviderWithNavigation` does: the explicit
 * Vite mode mapping first, then hostname detection.
 *
 * The order matters. react-auth's `getAuthConfig()` reads `window.import.meta.env.MODE`,
 * which is always undefined in a browser, so on any hostname outside its mapping table —
 * `localhost` included — it silently returns the Auth0 WeBuild AI config. Resolving by
 * mode first keeps the provider and the logout path in agreement about which identity
 * provider is actually in use.
 */
export function resolveAuthConfig(): AuthConfig | undefined {
  const mode = import.meta.env.MODE as string;
  const configKey = VITE_MODE_MAPPING[mode];

  return configKey ? getAuthConfigByKey(configKey) : getAuthConfig();
}

/**
 * Build the Azure AD end-session URL from the resolved tenant config.
 *
 * Returns `undefined` when the URL cannot be constructed, so the caller can still sign
 * the user out locally rather than sending them to a malformed address.
 */
function buildAzureLogoutUrl(authConfig: AuthConfig): string | undefined {
  try {
    const logoutUrl = new URL(getEndSessionEndpoint(authConfig));

    logoutUrl.searchParams.set("post_logout_redirect_uri", window.location.origin);
    logoutUrl.searchParams.set("client_id", authConfig.clientId);

    return logoutUrl.toString();
  } catch (error) {
    logger.error(
      "Could not build the Azure AD logout URL",
      error instanceof Error ? error : undefined,
      { domain: authConfig.domain },
    );

    return undefined;
  }
}

/**
 * Single logout entry point for every path that ends a Pathway session. The live callers are
 * the sidebar user menu, the admin-profile failure path in `App.tsx`, session expiry in
 * `main.tsx`, the tamper-detection force logout in `TamperDetection.tsx`, and the
 * `REQUEST_LOGOUT` postMessage handler in `IframeDashboard.tsx`. `navbar.tsx` also calls it,
 * though that component is not currently rendered.
 *
 * Do not call react-auth's `useAuth().logout()` directly from anywhere else. Doing so skips
 * the audit event, the session and cache clears, and the Azure workaround described below.
 *
 * Azure AD tenants deliberately avoid react-auth's `logout()`, which calls MSAL's
 * `logoutRedirect()`. MSAL rejects that call with `interaction_in_progress` whenever
 * interaction state is left in localStorage — by an interrupted redirect, or by a second
 * logout request arriving in the same tick — and react-auth does not catch the rejection,
 * so the logout silently does nothing. Clearing the caches and navigating to the
 * end-session endpoint cannot be blocked, and it clears any stuck state on the way out.
 *
 * Auth0 keeps react-auth's `logout()`, which is a plain redirect and is unaffected.
 */
export function useAppLogout(): (reason: LogoutReason) => void {
  const { logout } = useAuth();
  const { isEmbedded } = useEmbeddedAuthConfig();

  // Several paths can request a logout within the same tick: the warning modal countdown,
  // the session check interval, the cross-tab LOGOUT broadcast and the user's own click.
  // Only the first may proceed. The guard is per-mount so a remount can retry, and it is
  // released both when the attempt throws and when LOGOUT_WATCHDOG_MS elapses without the
  // browser navigating away.
  const logoutInFlight = useRef(false);

  // Read through refs so the returned callback can have an empty dependency array and a
  // permanently stable identity.
  //
  // react-auth builds its auth context value as a plain object literal on every render and
  // defines `logout` as an inline arrow inside it, so `useAuth().logout` is a new function
  // on every render of the provider. Depending on it directly would make this callback,
  // and therefore `SessionTimeoutWrapper`'s `onSessionExpired`, change identity constantly.
  // `useSessionManager` feeds that prop into the dependency array of its cross-tab effect,
  // whose setup and cleanup both write to the session store, so an unstable callback makes
  // that effect re-run and re-render in a loop until React aborts with "Maximum update
  // depth exceeded" and the session timer stops running. See the guard test in
  // `src/hooks/__tests__/sessionExpiry.repro.test.ts`.
  const logoutRef = useRef(logout);
  const isEmbeddedRef = useRef(isEmbedded);

  logoutRef.current = logout;
  isEmbeddedRef.current = isEmbedded;

  return useCallback(
    (reason: LogoutReason) => {
      const logout = logoutRef.current;
      const isEmbedded = isEmbeddedRef.current;

      // Drop the in-memory workflow and skill lists in every mode so they cannot leak
      // into the next session.
      clearWorkflowListCache();
      clearSkillListCache();

      // Embedded: the Pathway host owns the session. react-auth emits `embedded_logout`,
      // which EmbeddedAuthProvider turns into a REQUEST_LOGOUT message to the parent, so
      // there is no local interaction to serialise and repeat requests stay possible.
      if (isEmbedded) {
        logLogoutEvent(reason, { provider: "embedded" });
        logout();

        return;
      }

      if (logoutInFlight.current) {
        logger.debug("Logout already in progress — ignoring duplicate request", { reason });

        return;
      }
      logoutInFlight.current = true;

      const watchdog = window.setTimeout(() => {
        logoutInFlight.current = false;
        logger.warn("Logout did not navigate away, releasing the guard so the user can retry", {
          reason,
        });
      }, LOGOUT_WATCHDOG_MS);

      const releaseGuard = () => {
        window.clearTimeout(watchdog);
        logoutInFlight.current = false;
      };

      // Every provider path runs inside the same try so that a synchronous failure releases
      // the guard rather than wedging logout for the rest of the mount. `logout()` is typed
      // `() => void` in react-auth, so there is no promise rejection to chain onto here.
      try {
        const authConfig = resolveAuthConfig();

        logLogoutEvent(reason, { provider: authConfig?.provider ?? "unknown" });
        clearAppSessionData();

        if (authConfig?.provider !== "Azure") {
          logout();

          return;
        }

        clearMsalCache();
        redirectTo(buildAzureLogoutUrl(authConfig) ?? window.location.origin);
      } catch (error) {
        releaseGuard();
        logger.error("Logout failed", error instanceof Error ? error : undefined, { reason });
      }
    },
    // Intentionally empty. Everything that varies is read through a ref above, so this
    // callback keeps one identity for the life of the mount. See the comment on logoutRef.
    [],
  );
}

export default useAppLogout;
